import {
  type Currency,
  PAGE_AUDIENCES,
  type PageAudience,
  type StorefrontTheme,
} from "@loot-os/api-contract";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { cn } from "@loot-os/ui/lib/utils";
import { Monitor, Smartphone } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  PREVIEW_PAGES,
  PREVIEW_PATH,
  PREVIEW_VIEWPORTS,
  type PreviewPageId,
  type PreviewState,
  type PreviewViewport,
  parsePreviewReply,
  previewStateMessageOf,
} from "../lib/themePreview";

const PAGE_ITEMS: Record<string, string> = Object.fromEntries(
  PREVIEW_PAGES.map((page) => [page.id, page.label]),
);

/** How the audience switch names each audience. */
const AUDIENCE_LABELS: Record<PageAudience, string> = {
  signedIn: "Signed in",
  signedOut: "Signed out",
};

/**
 * The live preview the Theme tab and the Pages page frame: the dashboard's own `preview.html`,
 * sent the store's theme and layouts on every change, with a page picker and a desktop or phone
 * width. A desktop page is drawn at its real width and scaled to fit the panel, so its media
 * queries see a desktop. The frame loads only once the panel is on screen, so a closed tab never
 * fetches it.
 */
export function ThemePreviewPanel({
  theme,
  brand,
  currency,
  store,
  template,
  pageTemplates,
  page: shownPage,
  onPageChange,
  audience: shownAudience,
  onAudienceChange,
  className,
}: {
  /** The theme as it would be saved: the Theme tab's draft, or the saved one. */
  theme: StorefrontTheme;
  brand: { name: string; logoUrl: string };
  currency: Currency;
  store: PreviewState["store"];
  /** The layouts to draw: the storefront template, and per-page picks (the Pages page's draft). */
  template: PreviewState["template"];
  pageTemplates: PreviewState["pageTemplates"];
  /** The page shown, for a host that switches it (the Pages page); the panel's own otherwise. */
  page?: PreviewPageId;
  onPageChange?: (page: PreviewPageId) => void;
  /** Who Home is drawn for, for a host that switches it (the Pages page); the panel's own otherwise. */
  audience?: PageAudience;
  onAudienceChange?: (audience: PageAudience) => void;
  className?: string;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [ownPage, setOwnPage] = useState<PreviewPageId>("home");
  const page = shownPage ?? ownPage;
  const setPage = (next: PreviewPageId) => {
    setOwnPage(next);
    onPageChange?.(next);
  };
  const [ownAudience, setOwnAudience] = useState<PageAudience>("signedIn");
  const audience = shownAudience ?? ownAudience;
  const setAudience = (next: PageAudience) => {
    setOwnAudience(next);
    onAudienceChange?.(next);
  };
  const [viewport, setViewport] = useState<PreviewViewport>("desktop");
  // Bumped on every "ready": a frame that reloads (a dev reload) asks again and is sent the
  // state again, even though the panel already counted it ready once.
  const [readyCount, setReadyCount] = useState(0);
  const [boxSize, setBoxSize] = useState({ width: 0, height: 0 });
  // Each post is numbered and timed; the frame echoes the number once it has applied it.
  const sequence = useRef(0);
  const sentAt = useRef(new Map<number, number>());
  const [lastUpdateMs, setLastUpdateMs] = useState<number | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow) return;
      const reply = parsePreviewReply(event, window.location.origin);
      if (reply?.type === "loot-theme-preview:ready") setReadyCount((n) => n + 1);
      if (reply?.type === "loot-theme-preview:applied") {
        const started = sentAt.current.get(reply.sequence);
        sentAt.current.delete(reply.sequence);
        if (started !== undefined) setLastUpdateMs(performance.now() - started);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    const target = frame.current?.contentWindow;
    if (readyCount === 0 || !target) return;
    sequence.current += 1;
    sentAt.current.set(sequence.current, performance.now());
    target.postMessage(
      previewStateMessageOf({
        theme,
        page,
        brand,
        currency,
        store,
        template,
        pageTemplates,
        audience,
        sequence: sequence.current,
      }),
      window.location.origin,
    );
  }, [readyCount, theme, page, brand, currency, store, template, pageTemplates, audience]);

  useEffect(() => {
    const element = box.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setBoxSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // The tabs stay mounted, so a closed Theme tab still renders this panel, hidden. The frame gets
  // its page only once the panel has had a size, which a hidden one never does.
  const [shown, setShown] = useState(false);
  if (!shown && boxSize.width > 0) setShown(true);

  const { width } = PREVIEW_VIEWPORTS[viewport];
  const scale = boxSize.width > 0 ? Math.min(1, boxSize.width / width) : 1;

  return (
    <div className={cn("flex min-h-0 flex-col gap-3", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">Preview</span>
        <Select
          items={PAGE_ITEMS}
          value={page}
          onValueChange={(next) => {
            const match = PREVIEW_PAGES.find((entry) => entry.id === next);
            if (match) setPage(match.id);
          }}
        >
          <SelectTrigger size="sm" aria-label="Preview page" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PREVIEW_PAGES.map((entry) => (
              <SelectItem key={entry.id} value={entry.id}>
                {entry.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {page === "home" && (
          <fieldset className="flex rounded-lg border bg-muted/50 p-0.5">
            <legend className="sr-only">Preview as</legend>
            {PAGE_AUDIENCES.map((key) => (
              <label
                key={key}
                className="cursor-pointer rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground has-checked:bg-background has-checked:text-foreground has-checked:shadow-sm has-focus-visible:ring-2 has-focus-visible:ring-ring"
              >
                <input
                  type="radio"
                  name="theme-preview-audience"
                  value={key}
                  checked={audience === key}
                  onChange={() => setAudience(key)}
                  className="sr-only"
                />
                {AUDIENCE_LABELS[key]}
              </label>
            ))}
          </fieldset>
        )}
        <fieldset className="flex rounded-lg border bg-muted/50 p-0.5">
          <legend className="sr-only">Preview width</legend>
          {(Object.keys(PREVIEW_VIEWPORTS) as PreviewViewport[]).map((key) => {
            const Icon = key === "desktop" ? Monitor : Smartphone;
            return (
              <label
                key={key}
                title={PREVIEW_VIEWPORTS[key].label}
                className="cursor-pointer rounded-md p-1.5 text-muted-foreground transition-colors hover:text-foreground has-checked:bg-background has-checked:text-foreground has-checked:shadow-sm has-focus-visible:ring-2 has-focus-visible:ring-ring"
              >
                <input
                  type="radio"
                  name="theme-preview-viewport"
                  value={key}
                  checked={viewport === key}
                  onChange={() => setViewport(key)}
                  className="sr-only"
                />
                <Icon className="size-4" aria-hidden />
                <span className="sr-only">{PREVIEW_VIEWPORTS[key].label}</span>
              </label>
            );
          })}
        </fieldset>
        <span className="ml-auto text-xs text-muted-foreground">
          Sample content
          {import.meta.env.DEV &&
            lastUpdateMs !== null &&
            ` · updated in ${Math.round(lastUpdateMs)} ms`}
        </span>
      </div>
      <div
        ref={box}
        className="relative min-h-0 flex-1 overflow-hidden rounded-xl border bg-muted/30"
      >
        <iframe
          ref={frame}
          title="Storefront preview"
          src={shown ? PREVIEW_PATH : undefined}
          className="absolute top-0 left-1/2 origin-top border-0 bg-transparent"
          style={{
            width,
            height: scale > 0 ? boxSize.height / scale : boxSize.height,
            transform: `translateX(-50%) scale(${scale})`,
          }}
        />
      </div>
    </div>
  );
}
